import { db, auth } from '../firebase';
import { doc, setDoc, getDocs, collection, query, orderBy, limit, serverTimestamp, where, increment, updateDoc } from 'firebase/firestore';
import { incrementPetViewCount } from '@/services/petService';

export const useViewTracker = () => {
  const VIEW_COOLDOWN_MS = 20 * 60 * 1000; // 20 minutes

  const recordView = async (listing) => {
    if (!listing || !listing.id) return;
    
    // 1. Owner check
    const isOwner = auth.currentUser && auth.currentUser.uid === listing.createdBy;
    if (isOwner) return;

    // 2. Save to History (Always update history so it jumps to the top)
    await saveToHistory(listing);

    // 3. Debouncing check for View Count & Interests (Local Storage)
    const lsKey = `viewed_${listing.id}`;
    const lastViewed = localStorage.getItem(lsKey);
    const now = Date.now();

    if (lastViewed && (now - parseInt(lastViewed, 10)) < VIEW_COOLDOWN_MS) {
      // Already viewed recently, don't increment view count again
      return;
    }

    try {
      // 4. Update view count in Firestore
      await incrementPetViewCount(listing.id);
      localStorage.setItem(lsKey, now.toString());

      // 5. Update user interests (only if logged in)
      if (auth.currentUser) {
        await updateUserInterests(listing);
      }
    } catch (err) {
      console.error("Error recording view details:", err);
    }
  };

  const saveToHistory = async (listing) => {
    const historyItem = {
      listingId: listing.id,
      title: listing.title,
      price: listing.price,
      currency: listing.currency || '£',
      category: listing.category,
      coverImage: listing.images?.[listing.coverImageIndex || 0] || listing.images?.[0] || null,
      status: listing.status,
      viewedAt: Date.now()
    };

    if (auth.currentUser) {
      try {
        const historyRef = doc(db, 'users', auth.currentUser.uid, 'viewHistory', listing.id);
        await setDoc(historyRef, {
          ...historyItem,
          viewedAt: serverTimestamp()
        });
      } catch (err) {
        console.error("Error saving history to DB:", err);
      }
    } else {
      // Save to localStorage for guests
      try {
        let history = JSON.parse(localStorage.getItem('guest_view_history') || '[]');
        // Remove if already exists
        history = history.filter(item => item.listingId !== listing.id);
        // Add to front
        history.unshift(historyItem);
        // Limit to 100
        if (history.length > 100) history.pop();
        localStorage.setItem('guest_view_history', JSON.stringify(history));
      } catch (err) {
        console.error("Error saving guest history:", err);
      }
    }
  };

  const updateUserInterests = async (listing) => {
    if (!auth.currentUser || !listing.category) return;
    
    // Parse category strings (e.g. "Cats / British Shorthair")
    const parts = listing.category.split('/').map(s => s.trim());
    const mainCategory = parts[0];
    const subCategory = parts[1];

    const updates = {};
    if (mainCategory) {
      updates[`interests.${mainCategory.replace(/\\s+/g, '_')}`] = increment(1);
    }
    if (mainCategory && subCategory) {
      const combined = `${mainCategory}_${subCategory}`.replace(/\\s+/g, '_');
      updates[`interests.${combined}`] = increment(1);
    }

    if (Object.keys(updates).length > 0) {
      try {
        const userRef = doc(db, 'users', auth.currentUser.uid);
        await updateDoc(userRef, updates);
      } catch (err) {
        console.error("Error updating user interests:", err);
      }
    }
  };

  const getHistory = async () => {
    if (auth.currentUser) {
      try {
        const q = query(
          collection(db, 'users', auth.currentUser.uid, 'viewHistory'),
          orderBy('viewedAt', 'desc'),
          limit(100)
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
          ...doc.data(),
          viewedAt: doc.data().viewedAt?.toMillis ? doc.data().viewedAt.toMillis() : Date.now()
        }));
      } catch (err) {
        console.error("Error fetching view history from DB:", err);
        return [];
      }
    } else {
      try {
        return JSON.parse(localStorage.getItem('guest_view_history') || '[]');
      } catch (err) {
        return [];
      }
    }
  };

  const getRecommendations = async () => {
    let history = [];
    if (auth.currentUser) {
      history = await getHistory(); // Already fetches top 100
    } else {
      // Guests
      try {
        history = JSON.parse(localStorage.getItem('guest_view_history') || '[]');
      } catch (err) {
        history = [];
      }
    }

    if (!history || history.length === 0) return [];

    // Find the most frequent category
    const categoryCounts = {};
    history.forEach(item => {
      if (item.category) {
        categoryCounts[item.category] = (categoryCounts[item.category] || 0) + 1;
      }
    });

    const sortedCategories = Object.keys(categoryCounts).sort((a, b) => categoryCounts[b] - categoryCounts[a]);
    if (sortedCategories.length === 0) return [];

    const topCategory = sortedCategories[0];

    try {
      const q = query(
        collection(db, 'pets'),
        where('category', '==', topCategory),
        where('status', '==', 'active'),
        limit(50)
      );
      const snapshot = await getDocs(q);
      
      let recommendations = snapshot.docs.map(doc => ({
        listingId: doc.id,
        ...doc.data(),
        currency: doc.data().currency || '£',
        coverImage: doc.data().images?.[doc.data().coverImageIndex || 0] || doc.data().images?.[0] || null
      }));

      // Filter out own listings if logged in
      if (auth.currentUser) {
        recommendations = recommendations.filter(r => r.createdBy !== auth.currentUser.uid);
      }

      // Shuffle
      for (let i = recommendations.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [recommendations[i], recommendations[j]] = [recommendations[j], recommendations[i]];
      }

      return recommendations.slice(0, 10);
    } catch (err) {
      console.error("Error fetching recommendations:", err);
      return [];
    }
  };

  return { recordView, getHistory, getRecommendations };
};
